import { useState } from 'react';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { useConfirmation } from '../../../hooks/use-confirmation';
import { useRefreshCurrentUser } from '../../../hooks/use-refresh-current-user';
import { removePassword, setPassword } from '../security.api';

/** Setting, changing or removing the password, each behind "Confirm it's you" when the session needs it. */
export function usePassword(hasPassword: boolean) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { error, submitting, submit, clearError } = useSubmission();
  const confirmation = useConfirmation();
  const refreshUser = useRefreshCurrentUser();
  const toast = useToast();

  async function save(password: string) {
    const isSaved = await submit(
      () => confirmation.confirmed(hasPassword ? 'change your password' : 'set a password', () => setPassword(password).then(() => true)),
      (err) => apiErrorMessage(err) ?? "Couldn't save the password. Try again.",
    );
    if (!isSaved) return;
    await refreshUser();
    setIsFormOpen(false);
    toast.success(hasPassword ? 'Password changed' : 'Password set');
  }

  async function remove() {
    const isRemoved = await submit(
      () => confirmation.confirmed('remove your password', () => removePassword().then(() => true)),
      (err) => apiErrorMessage(err) ?? "Couldn't remove the password. Try again.",
    );
    if (!isRemoved) return;
    await refreshUser();
    toast.success('Password removed');
  }

  function closeForm() {
    setIsFormOpen(false);
    clearError();
  }

  return { isFormOpen, error, submitting, confirmation, openForm: () => setIsFormOpen(true), closeForm, save, remove };
}
